import { Injectable } from '@angular/core';
import { jsPDF } from 'jspdf';
import { AufnahmePunktDocument, istPunktartMitProtokoll, bodenformString, isB, isM, wasserformString, stfgString, roundToString, schichtLabel, BodenvegetationZeile } from '../../data-model';
import { schichtNWSKEval } from '../evaluators/nwsk';

@Injectable({ providedIn: 'root' })
export class ProtokollPdfService {

	/** Generiert ein PDF für einen oder mehrere Bohrpunkte */
	async generatePdf(bps: AufnahmePunktDocument[]): Promise<Blob> {
		console.log('[PDF] Starting PDF generation for', bps.length, 'points');

		const doc = new jsPDF({
			orientation: 'portrait',
			unit: 'mm',
			format: 'a4'
		});

		const pageWidth = doc.internal.pageSize.getWidth();
		const margin = 10;
		const contentWidth = pageWidth - 2 * margin;

		bps.forEach((bp, index) => {
			if (index > 0) {
				doc.addPage();
			}

			let y = margin;

			if (istPunktartMitProtokoll(bp.type)) {
				y = this.renderAllgemeinSection(doc, bp, margin, y, contentWidth);
				y += 6;
				y = this.renderBodenSection(doc, bp, margin, y, contentWidth);
				y += 4;
				y = this.renderZusammenfassung(doc, bp, margin, y, contentWidth);
				y += 4;
				y = this.renderVegetation(doc, bp, margin, y, contentWidth);
			} else {
				y = this.renderKurzprotokoll(doc, bp, margin, y, contentWidth);
			}
		});

		console.log('[PDF] PDF generation complete');
		return doc.output('blob');
	}

	/** Formatiert Datum von ISO zu dd.mm.yyyy */
	private formatDate(dateStr: string | undefined): string {
		if (!dateStr) return '';
		try {
			const date = new Date(dateStr);
			if (isNaN(date.getTime())) return dateStr;
			const day = date.getDate().toString().padStart(2, '0');
			const month = (date.getMonth() + 1).toString().padStart(2, '0');
			const year = date.getFullYear();
			return `${day}.${month}.${year}`;
		} catch {
			return dateStr;
		}
	}

	/** Rendert die Allgemein-Sektion als Tabelle */
	private renderAllgemeinSection(doc: jsPDF, bp: AufnahmePunktDocument, margin: number, startY: number, contentWidth: number): number {
		const h = bp.header;
		const eh = bp.extendedHeader;
		let y = startY;

		// Titel
		doc.setFontSize(11);
		doc.setFont('helvetica', 'bold');
		doc.text('Aufnahmepunkt: ' + (h.nummer || 'Ohne Nummer'), margin, y);
		y += 7;

		doc.setFontSize(8);
		const lineHeight = 4;
		const col1 = margin;
		const col2 = margin + 24;
		const col3 = margin + contentWidth / 2 + 5;
		const col4 = col3 + 24;

		// --- Block: Aufnahme (nur Hintergrund, keine Schrift) ---
		doc.setFillColor(235, 235, 235);
		doc.rect(margin, y, contentWidth, 3, 'F');
		y += 6; // Mehr Abstand nach grauem Balken

		y = this.drawTableRow(doc, y, lineHeight, col1, col2, col3, col4, contentWidth, margin,
			'Punktart', this.shortenPunktart(bp.type), 'Datum', this.formatDate(h.aufnahme?.datum));
		y = this.drawTableRow(doc, y, lineHeight, col1, col2, col3, col4, contentWidth, margin,
			'Nummer', h.nummer ? String(h.nummer) : '', 'Kartierer', h.aufnahme?.kartierer || '');
		y = this.drawTableRow(doc, y, lineHeight, col1, col2, col3, col4, contentWidth, margin,
			'Labornummer', h.labornummer ? String(h.labornummer) : '', '', '');
		// Laborstatus immer anzeigen (auch wenn leer)
		y = this.drawTableRow(doc, y, lineHeight, col1, col2, col3, col4, contentWidth, margin,
			'Laborstatus', h.statusLaboranalyse || '', '', '');

		y += 1;

		// --- Block: Adresse (nur Hintergrund) ---
		doc.setFillColor(235, 235, 235);
		doc.rect(margin, y, contentWidth, 3, 'F');
		y += 6; // Mehr Abstand nach grauem Balken

		y = this.drawTableRow(doc, y, lineHeight, col1, col2, col3, col4, contentWidth, margin,
			'Forstamt', h.forstamt || '', 'Revier', h.revier || '');
		y = this.drawTableRow(doc, y, lineHeight, col1, col2, col3, col4, contentWidth, margin,
			'Forstbetrieb', h.forstbetrieb || '', 'Abt/UA/TF', [h.adresse?.abteilung, h.adresse?.unterabteilung, h.adresse?.teilflaeche].filter(x => x).join('/'));
		y = this.drawTableRow(doc, y, lineHeight, col1, col2, col3, col4, contentWidth, margin,
			'Gem/Flur/FS', [h.adresse?.gemarkung, h.adresse?.flur, h.adresse?.flurstueck].filter(x => x).join('/'), '', '');

		y += 1;

		// --- Block: Naturraum (nur Hintergrund) ---
		doc.setFillColor(235, 235, 235);
		doc.rect(margin, y, contentWidth, 3, 'F');
		y += 6; // Mehr Abstand nach grauem Balken

		y = this.drawTableRow(doc, y, lineHeight, col1, col2, col3, col4, contentWidth, margin,
			'Wuchsgebiet', eh?.wuchsgebiet || '', 'Wuchsbezirk', eh?.wuchsbezirk || '');

		const geologie = [eh?.geologie?.oben, eh?.geologie?.trenner, eh?.geologie?.unten].filter(g => g).join(' ');
		const klimastufe = eh?.klimaStufe?.neu || eh?.klimaStufe?.alt || '';

		y = this.drawTableRow(doc, y, lineHeight, col1, col2, col3, col4, contentWidth, margin,
			'Geologie', geologie, 'Klimastufe', klimastufe);

		// Moortyp nur bei M-Punkten
		if (isM(bp) && eh?.moorTyp) {
			y = this.drawTableRow(doc, y, lineHeight, col1, col2, col3, col4, contentWidth, margin,
				'Moortyp (ökol.)', eh.moorTyp.oekologisch || '', 'Moortyp (hydr.)', eh.moorTyp.hydrogenetisch || '');
		}

		y = this.drawTableRow(doc, y, lineHeight, col1, col2, col3, col4, contentWidth, margin,
			'Relief', this.buildReliefString(bp), 'Mesoklima', eh?.mesoklima || '');

		// Morphologie und KMgCaP nur bei Nicht-Moor
		if (!isM(bp)) {
			const morphologie = eh?.morphologie?.filter(m => m).join('/') || '';
			y = this.drawTableRow(doc, y, lineHeight, col1, col2, col3, col4, contentWidth, margin,
				'Morphologie', morphologie, 'KMgCaP', eh?.kMgCaPSerie || '');
		}

		y = this.drawTableRow(doc, y, lineHeight, col1, col2, col3, col4, contentWidth, margin,
			'Witterung', eh?.witterung || '', '', '');

		y += 1;

		// --- Block: Bestand (nur Hintergrund) ---
		doc.setFillColor(235, 235, 235);
		doc.rect(margin, y, contentWidth, 3, 'F');
		y += 6; // Mehr Abstand nach grauem Balken

		const baumartenStr = this.formatBaumarten(eh?.baumarten);
		if (baumartenStr) {
			doc.setFont('helvetica', 'bold');
			doc.text('Bestand:', col1, y);
			doc.setFont('helvetica', 'normal');
			const baumartenLines = doc.splitTextToSize(baumartenStr, contentWidth - 25);
			doc.text(baumartenLines, col2, y);
			y += Math.max(baumartenLines.length * lineHeight, lineHeight);
		}

		// Bemerkungen immer anzeigen (auch wenn leer)
		doc.setFont('helvetica', 'bold');
		doc.text('Bemerkungen:', col1, y);
		doc.setFont('helvetica', 'normal');
		if (h.bemerkungen) {
			const bemLines = doc.splitTextToSize(h.bemerkungen, contentWidth - 25);
			doc.text(bemLines, col2, y);
			y += Math.max(bemLines.length * lineHeight, lineHeight);
		} else {
			y += lineHeight;
		}

		// Linie unter Allgemein
		doc.setDrawColor(150);
		doc.line(margin, y, margin + contentWidth, y);

		return y;
	}

	/** Formatiert Baumarten für die Anzeige */
	private formatBaumarten(baumarten: any): string {
		if (!baumarten) return '';
		if (typeof baumarten === 'string') return baumarten;
		if (Array.isArray(baumarten)) {
			return baumarten.map(b => `${b.art || ''} ${b.alter || ''}j`).filter(s => s.trim()).join(', ');
		}
		return '';
	}

	/** Zeichnet eine Tabellenzeile mit 4 Spalten */
	private drawTableRow(doc: jsPDF, y: number, lineHeight: number, col1: number, col2: number, col3: number, col4: number, contentWidth: number, margin: number,
		label1: string, value1: string, label2: string, value2: string): number {
		doc.setFont('helvetica', 'bold');
		doc.text(label1 + ':', col1, y);
		doc.setFont('helvetica', 'normal');
		doc.text(value1, col2, y);
		if (label2) {
			doc.setFont('helvetica', 'bold');
			doc.text(label2 + ':', col3, y);
			doc.setFont('helvetica', 'normal');
			doc.text(value2, col4, y);
		}
		return y + lineHeight;
	}

	/** Kürzt die Punktart (entfernt den Prefix) */
	private shortenPunktart(type: string | undefined): string {
		if (!type) return '';
		// z.B. "6_Tiefbohrung" -> "Tiefbohrung"
		const idx = type.indexOf('_');
		return idx > 0 ? type.substring(idx + 1) : type;
	}

	/** Rendert die Boden-Sektion (Substrat- und Horizontfolge) */
	private renderBodenSection(doc: jsPDF, bp: AufnahmePunktDocument, margin: number, startY: number, contentWidth: number): number {
		const substratfolge = bp.Substratfolge || [];
		const horizontfolge = bp.Horizontfolge || [];
		let y = startY;

		const isMoor = isM(bp);
		const isBohr = isB(bp);
		const rowHeight = 3.5;
		const tableEndX = margin + contentWidth;

		// Spaltenbreiten gleichmäßig verteilen
		// Substrat: ca. 65% der Breite, Horizont: ca. 35%
		const substratWidth = contentWidth * 0.65;
		const horizontWidth = contentWidth * 0.35 - 2; // -2 für Trennlinie

		// B-Punkte (isB): Tiefe, Mächt, W, Substrat, Humus, LagD, Skel, Karb, nWSK (9 Spalten, KEINE Farbe)
		// G-Punkte (!isB && !isM): wie B aber MIT Farbe (10 Spalten)
		// M-Punkte (isM): Tiefe, Mächt, W, T1, T2, T3, Substrat, Zers, Kons, Skel, Karb, SubV, Bes, Farbe, nWSK (15 Spalten)
		let headers: string[];
		let colCount: number;
		let sColWidths: number[]; // Individuelle Spaltenbreiten

		// Feste schmale Breiten für Tiefe, Mächtigkeit und W (mit Platz nach Mächtigkeit)
		const tiefeWidth = 7;
		const maechtWidth = 10; // Abstand nach rechts vor W
		const wWidth = 8; // Breiter für '<->' Symbol
		const fixedWidth = tiefeWidth + maechtWidth + wWidth;

		if (isMoor) {
			colCount = 15;
			headers = ['Tiefe', 'Mächt', 'W', 'T1', 'T2', 'T3', 'Substrat', 'Zers', 'Kons', 'Skel', 'Karb', 'SubV', 'Bes.', 'Farbe', 'nWSK'];
			const remainingWidth = substratWidth - fixedWidth;
			const otherColWidth = remainingWidth / 12; // 12 weitere Spalten
			sColWidths = [tiefeWidth, maechtWidth, wWidth, otherColWidth, otherColWidth, otherColWidth, otherColWidth, otherColWidth, otherColWidth, otherColWidth, otherColWidth, otherColWidth, otherColWidth, otherColWidth, otherColWidth];
		} else if (isBohr) {
			colCount = 9;
			headers = ['Tiefe', 'Mächt', 'W', 'Substrat', 'Humus', 'LagD', 'Skel', 'Karb', 'nWSK'];
			const remainingWidth = substratWidth - fixedWidth;
			const otherColWidth = remainingWidth / 6; // 6 weitere Spalten
			sColWidths = [tiefeWidth, maechtWidth, wWidth, otherColWidth, otherColWidth, otherColWidth, otherColWidth, otherColWidth, otherColWidth];
		} else {
			colCount = 10;
			headers = ['Tiefe', 'Mächt', 'W', 'Substrat', 'Humus', 'LagD', 'Skel', 'Karb', 'Farbe', 'nWSK'];
			const remainingWidth = substratWidth - fixedWidth;
			const otherColWidth = remainingWidth / 7; // 7 weitere Spalten
			sColWidths = [tiefeWidth, maechtWidth, wWidth, otherColWidth, otherColWidth, otherColWidth, otherColWidth, otherColWidth, otherColWidth, otherColWidth];
		}

		// Spaltenpositionen aus individuellen Breiten berechnen
		const sColPos: number[] = [];
		let currentX = margin;
		for (let i = 0; i < colCount; i++) {
			sColPos.push(currentX);
			currentX += sColWidths[i];
		}
		const substratEndX = margin + substratWidth;

		// Horizont: 5 Spalten - Tiefe, Mächt, H1, H2, H3 (schmale Tiefe/Mächt, Rest gleichmäßig)
		const hStartX = substratEndX + 2;
		const hColCount = 5;
		const hTiefeWidth = 7;
		const hMaechtWidth = 12; // Breiter für mehr Abstand nach rechts vor H1
		const hFixedWidth = hTiefeWidth + hMaechtWidth;
		const hOtherColWidth = (horizontWidth - hFixedWidth) / 3; // 3 Horizont-Spalten
		const hColWidths = [hTiefeWidth, hMaechtWidth, hOtherColWidth, hOtherColWidth, hOtherColWidth];
		const hColPos: number[] = [];
		let hCurrentX = hStartX;
		for (let i = 0; i < hColCount; i++) {
			hColPos.push(hCurrentX);
			hCurrentX += hColWidths[i];
		}

		const hHeaders = ['Tiefe', 'Mächt', 'H1', 'H2', 'H3'];

		// Überschriften für die Tabellenbereiche
		doc.setFontSize(7);
		doc.setFont('helvetica', 'bold');
		doc.text('Substratfolge', margin + (substratEndX - margin) / 2, y, { align: 'center' });
		doc.text('Horizontfolge', hStartX + (tableEndX - hStartX) / 2, y, { align: 'center' });
		y += 3;

		// Header-Zeile mit grauem Hintergrund
		doc.setFillColor(210, 210, 210);
		doc.rect(margin, y - 2.5, substratEndX - margin, 3.5, 'F');
		doc.rect(hStartX, y - 2.5, tableEndX - hStartX, 3.5, 'F');

		// Spaltenheader
		doc.setFontSize(6.5);
		headers.forEach((hdr, i) => {
			if (i < sColPos.length) {
				// nWSK (letzte Spalte) rechtsbündig
				if (i === colCount - 1) {
					doc.text(hdr, sColPos[i] + sColWidths[i] - 2, y, { align: 'right' });
				} else {
					doc.text(hdr, sColPos[i] + 1, y);
				}
			}
		});
		hHeaders.forEach((hdr, i) => doc.text(hdr, hColPos[i] + 1, y));

		y += 1;
		// Linie unter Header
		doc.setDrawColor(100);
		doc.line(margin, y, tableEndX, y);
		// Vertikale Trennlinie im Header
		doc.line(substratEndX + 1, y - 6.5, substratEndX + 1, y);

		const headerEndY = y;
		y += rowHeight;

		doc.setFont('helvetica', 'normal');
		doc.setFontSize(7);

		// Merged-Rows: Substrat und Horizont haben unabhängige Zeilenzahlen
		const mergedRows = this.buildMergedRows(substratfolge, horizontfolge);

		mergedRows.forEach((row) => {
			const s = row.substrat;
			const hz = row.horizont;

			// Striped background - getrennt für Substrat und Horizont basierend auf deren Index
			if (row.substratStripe) {
				doc.setFillColor(242, 242, 242);
				doc.rect(margin, y - 2.5, substratEndX - margin, rowHeight, 'F');
			}
			if (row.horizontStripe) {
				doc.setFillColor(242, 242, 242);
				doc.rect(hStartX, y - 2.5, tableEndX - hStartX, rowHeight, 'F');
			}

			// Substrat-Daten (nur wenn Substrat-Eintrag vorhanden)
			if (s && row.substratIndex !== null) {
				// Tiefe und Mächtigkeit rechtsbündig mit Abstand vom Rand (Mächtigkeit mit mehr Abstand zu W)
				doc.text(s.tiefe?.toString() || '', sColPos[0] + sColWidths[0] - 1, y, { align: 'right' });
				doc.text(s.maechtigkeit?.toString() || '', sColPos[1] + sColWidths[1] - 5, y, { align: 'right' });
				doc.text(s.wechselschichtung ? 'W' : '', sColPos[2] + 1, y);

				// nWSK über Evaluator berechnen
				const nWskValue = this.schichtNWSK(bp, row.substratIndex);
				// nWSK rechtsbündig am rechten Rand der Zelle (letzte Spalte), mit Abstand vom Rand
				const nWskColRight = sColPos[colCount - 1] + sColWidths[colCount - 1] - 2;

				if (isMoor) {
					// M-Punkte: T1, T2, T3, Substrat, Zers, Kons, Skel, Karb, SubV, Bes, Farbe, nWSK
					doc.text(s.torfarten?.[0] || '', sColPos[3] + 1, y);
					doc.text(s.torfarten?.[1] || '', sColPos[4] + 1, y);
					doc.text(s.torfarten?.[2] || '', sColPos[5] + 1, y);
					doc.text(s.substrat || '', sColPos[6] + 1, y);
					doc.text(s.torfZersetzungsgrad?.toString() || '', sColPos[7] + 1, y);
					doc.text(s.muddeKonsistenz?.toString() || '', sColPos[8] + 1, y);
					doc.text(s.skelettgehalt?.toString() || '', sColPos[9] + 1, y);
					doc.text(s.karbonatgehalt?.toString() || '', sColPos[10] + 1, y);
					doc.text(s.substanzvolumen?.toString() || '', sColPos[11] + 1, y);
					const bes = (s.besonderheiten || '').substring(0, 6);
					doc.text(bes, sColPos[12] + 1, y);
					doc.text(s.farbe || '', sColPos[13] + 1, y);
					// nWSK rechtsbündig
					doc.text(nWskValue, nWskColRight, y, { align: 'right' });
				} else if (isBohr) {
					// B-Punkte: Substrat, Humus, LagD, Skel, Karb, nWSK (ohne Farbe)
					doc.text(s.substrat || '', sColPos[3] + 1, y);
					doc.text(s.humusgehalt?.toString() || '', sColPos[4] + 1, y);
					doc.text(s.lagerungsdichte?.toString() || '', sColPos[5] + 1, y);
					doc.text(s.skelettgehalt?.toString() || '', sColPos[6] + 1, y);
					doc.text(s.karbonatgehalt?.toString() || '', sColPos[7] + 1, y);
					// nWSK rechtsbündig
					doc.text(nWskValue, nWskColRight, y, { align: 'right' });
				} else {
					// G-Punkte: Substrat, Humus, LagD, Skel, Karb, Farbe, nWSK
					doc.text(s.substrat || '', sColPos[3] + 1, y);
					doc.text(s.humusgehalt?.toString() || '', sColPos[4] + 1, y);
					doc.text(s.lagerungsdichte?.toString() || '', sColPos[5] + 1, y);
					doc.text(s.skelettgehalt?.toString() || '', sColPos[6] + 1, y);
					doc.text(s.karbonatgehalt?.toString() || '', sColPos[7] + 1, y);
					doc.text(s.farbe || '', sColPos[8] + 1, y);
					// nWSK rechtsbündig
					doc.text(nWskValue, nWskColRight, y, { align: 'right' });
				}
			}

			// Horizont-Daten (nur wenn Horizont-Eintrag vorhanden)
			if (hz) {
				// Tiefe und Mächtigkeit rechtsbündig mit Abstand vom Rand (Mächtigkeit mit mehr Abstand zu H1)
				doc.text(hz.tiefe?.toString() || '', hColPos[0] + hColWidths[0] - 1, y, { align: 'right' });
				doc.text(hz.maechtigkeit?.toString() || '', hColPos[1] + hColWidths[1] - 5, y, { align: 'right' });
				const horizonte = hz.horizonte || [];
				// 3 Horizont-Spalten separat
				doc.text(horizonte[0] || '', hColPos[2] + 1, y);
				doc.text(horizonte[1] || '', hColPos[3] + 1, y);
				doc.text(horizonte[2] || '', hColPos[4] + 1, y);
			}

			y += rowHeight;
		});

		// Unterlinie
		doc.setDrawColor(150);
		doc.line(margin, y, tableEndX, y);
		// Vertikale Trennlinie durchgehend
		doc.line(substratEndX + 1, headerEndY, substratEndX + 1, y);

		return y + 2;
	}

	/** Baut Merged-Rows für Substrat/Horizont-Tabelle (analog zur Angular-Komponente) */
	private buildMergedRows(substratfolge: any[], horizontfolge: any[]): {substrat: any, horizont: any, substratIndex: number | null, substratStripe: boolean, horizontStripe: boolean}[] {
		// Tiefen mit Indizes sammeln
		const substratTiefen = substratfolge.map((s, i) => ({ tiefe: s.tiefe, index: i }));
		const horizontTiefen = horizontfolge.map((h, i) => ({ tiefe: h.tiefe, index: i }));

		// Alle einzigartigen Tiefen sammeln und sortieren
		const alleTiefen = new Set<number>();
		substratTiefen.forEach(s => { if (s.tiefe !== undefined) alleTiefen.add(s.tiefe); });
		horizontTiefen.forEach(h => { if (h.tiefe !== undefined) alleTiefen.add(h.tiefe); });

		const sortedTiefen = Array.from(alleTiefen).sort((a, b) => a - b);

		const result: {substrat: any, horizont: any, substratIndex: number | null, substratStripe: boolean, horizontStripe: boolean}[] = [];

		// Tracke, welche Indizes bereits verwendet wurden
		const usedSubstratIndices = new Set<number>();
		const usedHorizontIndices = new Set<number>();

		// Erste Zeile: Immer Index 0 für beide (falls vorhanden)
		const firstSubstrat = substratTiefen.length > 0 ? substratTiefen[0] : null;
		const firstHorizont = horizontTiefen.length > 0 ? horizontTiefen[0] : null;

		if (firstSubstrat || firstHorizont) {
			if (firstSubstrat) usedSubstratIndices.add(0);
			if (firstHorizont) usedHorizontIndices.add(0);

			result.push({
				substrat: firstSubstrat ? substratfolge[0] : null,
				horizont: firstHorizont ? horizontfolge[0] : null,
				substratIndex: firstSubstrat ? 0 : null,
				substratStripe: false,
				horizontStripe: false
			});
		}

		// Restliche Zeilen nach Tiefe sortiert
		for (const tiefe of sortedTiefen) {
			const substrat = substratTiefen.find(s => s.tiefe === tiefe && !usedSubstratIndices.has(s.index));
			const horizont = horizontTiefen.find(h => h.tiefe === tiefe && !usedHorizontIndices.has(h.index));

			// Überspringe, wenn beide null sind
			if (!substrat && !horizont) continue;

			if (substrat) usedSubstratIndices.add(substrat.index);
			if (horizont) usedHorizontIndices.add(horizont.index);

			result.push({
				substrat: substrat ? substratfolge[substrat.index] : null,
				horizont: horizont ? horizontfolge[horizont.index] : null,
				substratIndex: substrat ? substrat.index : null,
				substratStripe: false,
				horizontStripe: false
			});
		}

		// Stripes berechnen: Von unten nach oben
		// Echte Zeilen bekommen abwechselnd Farben, Leerzeilen bekommen die Farbe der echten Zeile DARUNTER
		let currentSubstratStripe = false;
		let currentHorizontStripe = false;

		for (let i = result.length - 1; i >= 0; i--) {
			const row = result[i];

			// Substrat: Erst wechseln bei echter Zeile, dann Farbe zuweisen
			if (row.substrat !== null) {
				currentSubstratStripe = !currentSubstratStripe;
			}
			row.substratStripe = currentSubstratStripe;

			// Horizont: gleiche Logik
			if (row.horizont !== null) {
				currentHorizontStripe = !currentHorizontStripe;
			}
			row.horizontStripe = currentHorizontStripe;
		}

		return result;
	}

	/** Berechnet nWSK für eine Substratschicht direkt über die Evaluator-Funktion */
	private schichtNWSK(bp: AufnahmePunktDocument, index: number): string {
		const result = schichtNWSKEval(bp, index) as number | undefined;
		if (result === undefined || result === null || isNaN(result)) return '';
		return Math.round(result).toString();
	}

	/** Rendert die Zusammenfassung (HBF, FBF, nWSK, Humusauflage etc.) */
	private renderZusammenfassung(doc: jsPDF, bp: AufnahmePunktDocument, margin: number, startY: number, contentWidth: number): number {
		const h = bp.header;
		const eh = bp.extendedHeader;
		let y = startY;

		doc.setFontSize(7);
		const lineHeight = 3.5;

		// Spalten basierend auf 11er-Grid wie im HTML (siehe bohrpunkt-boden.component.html)
		// HBF/FBF/HF | Wert(colspan2) | Ol/Of/Oh | Wert | Wasserform/Saugsaum/Spiegeltiefe | Wert | pH/V-Wert/Karbonat | Wert | Entw/nWSK/Stufe | Wert
		const colWidth = contentWidth / 11;
		const c1 = margin;                    // Label 1 (HBF, FBF, HF, St.-STFG)
		const c2 = margin + colWidth;         // Wert 1 (breit, colspan=2)
		const c4 = margin + colWidth * 3;     // Label 2 (Ol, Of, Oh)
		const c5 = margin + colWidth * 4;     // Wert 2
		const c6 = margin + colWidth * 5;     // Label 3 (Wasserform, Saugsaum, Spiegeltiefe)
		const c7 = margin + colWidth * 6.2;   // Wert 3
		const c8 = margin + colWidth * 7.2;   // Label 4 (pH, V-Wert, Karbonattiefe)
		const c9 = margin + colWidth * 8.5;   // Wert 4
		const c10 = margin + colWidth * 9;    // Label 5 (Entw., nWSK, nWSK-Stufe)
		const c11 = margin + colWidth * 10.2; // Wert 5

		// Zeile 1: HBF | Ol | Wasserform | pH | Entw.-verhalten
		doc.setFont('helvetica', 'bold');
		doc.text('HBF', c1, y);
		doc.setFont('helvetica', 'normal');
		doc.text(bodenformString(h.bodenform, 'hbf') || '', c2, y);

		doc.setFont('helvetica', 'bold');
		doc.text('Ol', c4, y);
		doc.setFont('helvetica', 'normal');
		doc.text(eh?.humusAuflage?.ol?.toString() || '', c5, y);

		doc.setFont('helvetica', 'bold');
		doc.text('Wasserform', c6, y);
		doc.setFont('helvetica', 'normal');
		doc.text(wasserformString(bp) || '', c7, y);

		doc.setFont('helvetica', 'bold');
		const ph1Lbl = eh?.ph?.ort1 === 'Saugsaum' ? 'pH(SS)' : 'pH(OB)';
		doc.text(ph1Lbl, c8, y);
		doc.setFont('helvetica', 'normal');
		doc.text(eh?.ph?.wert1?.toString() || '', c9, y);

		doc.setFont('helvetica', 'bold');
		doc.text('Entw.-verh.', c10, y);
		doc.setFont('helvetica', 'normal');
		doc.text(eh?.entwaesserungsverhalten ? String(eh.entwaesserungsverhalten) : '', c11, y);

		y += lineHeight;

		// Zeile 2: FBF | Of | Mesoklima | V-Wert | nWSK
		doc.setFont('helvetica', 'bold');
		doc.text('FBF', c1, y);
		doc.setFont('helvetica', 'normal');
		// FBF mit Mesoklima-Suffix (wie in Komponente)
		const fbf = bodenformString(h.bodenform, 'fbf') || '';
		const mesoklima = eh?.mesoklima ? String(eh.mesoklima) : '';
		const fbfMitMesoklima = mesoklima ? `${fbf}_${mesoklima}` : fbf;
		doc.text(fbfMitMesoklima, c2, y);

		doc.setFont('helvetica', 'bold');
		doc.text('Of', c4, y);
		doc.setFont('helvetica', 'normal');
		doc.text(eh?.humusAuflage?.of?.toString() || '', c5, y);

		doc.setFont('helvetica', 'bold');
		doc.text('Mesoklima', c6, y);
		doc.setFont('helvetica', 'normal');
		doc.text(mesoklima, c7, y);

		doc.setFont('helvetica', 'bold');
		doc.text('V-Wert', c8, y);
		doc.setFont('helvetica', 'normal');
		const vWert = eh?.vWertSaugsaum ? eh.vWertSaugsaum.toString() : (eh?.vWertSaugsaumVermutet ? String(eh.vWertSaugsaumVermutet) : '');
		doc.text(vWert, c9, y);

		doc.setFont('helvetica', 'bold');
		doc.text('nWSK', c10, y);
		doc.setFont('helvetica', 'normal');
		doc.text(roundToString(eh?.nWsk, 2) || '', c11, y);

		y += lineHeight;

		// Zeile 3: HF/VegFG | Oh | Saugsaum | Karbonattiefe | nWSK-Stufe
		const humusformNeu = h.humusform?.aufnahmeFlaeche?.neu || h.humusform?.aufnahmeFlaeche?.alt || '';
		const hfLbl = isM(bp) ? 'VegFG' : 'HF';
		const hfVal = isM(bp) ? (h.vegetationsformengruppe ? String(h.vegetationsformengruppe) : '') : humusformNeu;

		doc.setFont('helvetica', 'bold');
		doc.text(hfLbl, c1, y);
		doc.setFont('helvetica', 'normal');
		doc.text(hfVal, c2, y);

		doc.setFont('helvetica', 'bold');
		doc.text('Oh', c4, y);
		doc.setFont('helvetica', 'normal');
		doc.text(eh?.humusAuflage?.oh?.toString() || '', c5, y);

		doc.setFont('helvetica', 'bold');
		doc.text('Saugsaum', c6, y);
		doc.setFont('helvetica', 'normal');
		doc.text(eh?.saugsaum?.toString() || '', c7, y);

		doc.setFont('helvetica', 'bold');
		doc.text('Karbonattiefe', c8, y);
		doc.setFont('helvetica', 'normal');
		const karbonatiefe = this.getKarbonattiefe(bp);
		doc.text(karbonatiefe, c9, y);

		doc.setFont('helvetica', 'bold');
		doc.text('nWSK-Stufe', c10, y);
		doc.setFont('helvetica', 'normal');
		doc.text(eh?.nWskStufe ? String(eh.nWskStufe) : '', c11, y);

		y += lineHeight;

		// Zeile 4: St.-STFG | | Spiegeltiefe | | Substratfeuchte
		doc.setFont('helvetica', 'bold');
		doc.text('St.-STFG', c1, y);
		doc.setFont('helvetica', 'normal');
		doc.text(stfgString(bp), c2, y);

		doc.setFont('helvetica', 'bold');
		doc.text('Spiegeltiefe', c6, y);
		doc.setFont('helvetica', 'normal');
		doc.text(eh?.spiegelTiefe?.toString() || '', c7, y);

		doc.setFont('helvetica', 'bold');
		doc.text('Substratfeuchte', c10, y);
		doc.setFont('helvetica', 'normal');
		doc.text(h.substratFeuchte ? String(h.substratFeuchte) : '', c11, y);

		y += lineHeight + 2;

		return y;
	}

	/** Berechnet die Karbonattiefe aus der ersten Substratzeile mit Karbonat > 0 */
	private getKarbonattiefe(bp: AufnahmePunktDocument): string {
		const substratfolge = bp.Substratfolge || [];
		for (const s of substratfolge) {
			if (s.karbonatgehalt && s.karbonatgehalt > 0) {
				return s.tiefe?.toString() || '';
			}
		}
		return '';
	}

	/** Rendert das Kurzprotokoll für Punkte ohne vollständiges Protokoll */
	private renderKurzprotokoll(doc: jsPDF, bp: AufnahmePunktDocument, margin: number, startY: number, contentWidth: number): number {
		const h = bp.header;
		let y = startY;

		doc.setFontSize(11);
		doc.setFont('helvetica', 'bold');
		doc.text('Aufnahmepunkt: ' + (h.nummer || 'Ohne Nummer'), margin, y);
		y += 7;

		doc.setFontSize(9);
		const col1 = margin;
		const col2 = margin + 28;
		const lineHeight = 5;

		doc.setFont('helvetica', 'bold');
		doc.text('Punktart:', col1, y);
		doc.setFont('helvetica', 'normal');
		doc.text(this.shortenPunktart(bp.type), col2, y);
		y += lineHeight;

		doc.setFont('helvetica', 'bold');
		doc.text('Datum:', col1, y);
		doc.setFont('helvetica', 'normal');
		doc.text(this.formatDate(h.aufnahme?.datum), col2, y);
		y += lineHeight;

		doc.setFont('helvetica', 'bold');
		doc.text('Kartierer:', col1, y);
		doc.setFont('helvetica', 'normal');
		doc.text(h.aufnahme?.kartierer || '', col2, y);
		y += lineHeight;

		if (h.koordinaten) {
			doc.setFont('helvetica', 'bold');
			doc.text('Koordinaten:', col1, y);
			doc.setFont('helvetica', 'normal');
			doc.text(`${String(h.koordinaten.x)} / ${String(h.koordinaten.y)}`, col2, y);
			y += lineHeight;
		}

		if (h.bemerkungen) {
			doc.setFont('helvetica', 'bold');
			doc.text('Bemerkungen:', col1, y);
			y += lineHeight;
			doc.setFont('helvetica', 'normal');
			const bemLines = doc.splitTextToSize(h.bemerkungen, contentWidth - 5);
			doc.text(bemLines, col1 + 2, y);
			y += bemLines.length * lineHeight;
		}

		return y;
	}

	/** Baut Relief-String für B-Punkte */
	private buildReliefString(bp: AufnahmePunktDocument): string {
		const eh = bp.extendedHeader;
		if (!eh?.relief) return '';

		if (isB(bp)) {
			// B-Punkte: Erweiterte Relief-Angaben
			const parts = [];
			if (eh.relief.neigungsstufe) parts.push(eh.relief.neigungsstufe);
			if (eh.relief.hangrichtung) parts.push(eh.relief.hangrichtung);
			return parts.join(', ') || eh.relief.relief || '';
		} else {
			// M-Punkte: Moor-Relief
			const parts = [];
			if (eh.relief.reliefBestand) parts.push(eh.relief.reliefBestand);
			if (eh.relief.reliefBodenvegetation) parts.push(eh.relief.reliefBodenvegetation);
			return parts.join(' / ');
		}
	}

	/** Rendert die Vegetationsaufnahme */
	private renderVegetation(doc: jsPDF, bp: AufnahmePunktDocument, margin: number, startY: number, contentWidth: number): number {
		const veg = bp.bodenvegetation;
		if (!veg?.aufnahme?.vegetationen?.length && !this.hasSchichtAnteile(veg)) {
			return startY; // Keine Vegetation vorhanden
		}

		let y = startY;
		const rowHeight = 3.5;

		// Überschrift
		doc.setFontSize(9);
		doc.setFont('helvetica', 'bold');
		doc.text('Vegetationsaufnahme', margin, y);
		y += 4;

		// Schichtanteile
		if (this.hasSchichtAnteile(veg)) {
			doc.setFontSize(7);
			doc.setFillColor(220, 220, 220);
			doc.rect(margin, y - 2.5, contentWidth, 3.5, 'F');
			doc.text('Schichtanteile', margin + 1, y);
			y += 3;

			doc.setFont('helvetica', 'normal');
			doc.setFontSize(7);
			const anteile = veg.aufnahme.schichtAnteile;
			const schichten: ('b1' | 'b2' | 'str' | 'kr1' | 'kr2' | 'm')[] = ['b1', 'b2', 'str', 'kr1', 'kr2', 'm'];
			let xPos = margin;
			const colWidth = contentWidth / 6;

			schichten.forEach(sch => {
				const val = anteile[sch];
				if (val !== undefined && val !== null) {
					doc.setFont('helvetica', 'bold');
					doc.text(schichtLabel(sch) + ':', xPos, y);
					doc.setFont('helvetica', 'normal');
					doc.text(val.toString(), xPos + 18, y);
				}
				xPos += colWidth;
			});
			y += rowHeight + 1;
		}

		// Vegetationstabelle
		const vegetationen = veg?.aufnahme?.vegetationen || [];
		if (vegetationen.length > 0) {
			// Header
			doc.setFontSize(7);
			doc.setFont('helvetica', 'bold');
			doc.setFillColor(220, 220, 220);
			doc.rect(margin, y - 2.5, contentWidth, 3.5, 'F');

			const col1 = margin + 1;
			const col2 = margin + 25;
			const col3Right = margin + contentWidth - 2; // Rechtsbündig mit Abstand vom Rand
			doc.text('Schicht', col1, y);
			doc.text('Vegetation', col2, y);
			doc.text('%', col3Right, y, { align: 'right' });

			y += 1;
			doc.setDrawColor(100);
			doc.line(margin, y, margin + contentWidth, y);
			y += rowHeight;

			doc.setFont('helvetica', 'normal');

			// Vegetationen nach Schicht gruppieren und sortieren
			const sortedVeg = this.sortVegetationen(vegetationen);

			sortedVeg.forEach((v, idx) => {
				// Striped
				if (idx % 2 === 0) {
					doc.setFillColor(245, 245, 245);
					doc.rect(margin, y - 2.5, contentWidth, rowHeight, 'F');
				}

				const schichtStr = v.schicht ? schichtLabel(v.schicht) : '';
				doc.text(schichtStr, col1, y);
				doc.text(v.vegetation || '', col2, y);
				// haeufigkeit kann string oder number sein, rechtsbündig mit Abstand
				const haeufStr = v.haeufigkeit !== undefined && v.haeufigkeit !== null ? String(v.haeufigkeit) : '';
				doc.text(haeufStr, col3Right, y, { align: 'right' });
				y += rowHeight;
			});

			// Unterlinie
			doc.setDrawColor(150);
			doc.line(margin, y - rowHeight + 2.5, margin + contentWidth, y - rowHeight + 2.5);
		}

		return y;
	}

	/** Prüft ob Schichtanteile vorhanden sind */
	private hasSchichtAnteile(veg: any): boolean {
		if (!veg?.aufnahme?.schichtAnteile) return false;
		const a = veg.aufnahme.schichtAnteile;
		return !!(a.b1 || a.b2 || a.str || a.kr1 || a.kr2 || a.m);
	}

	/** Sortiert Vegetationen nach Schicht */
	private sortVegetationen(vegetationen: BodenvegetationZeile[]): BodenvegetationZeile[] {
		const order: Record<string, number> = { 'b1': 1, 'b2': 2, 'str': 3, 'kr1': 4, 'kr2': 5, 'm': 6 };
		return [...vegetationen].sort((a, b) => {
			const aOrder = a.schicht ? (order[a.schicht] || 99) : 99;
			const bOrder = b.schicht ? (order[b.schicht] || 99) : 99;
			return aOrder - bOrder;
		});
	}
}
